<template>
    <div class="flexbox-fullheight form-view">
        <div class="asset-container full-height">
            <header class="asset-container-header">
                <div class="grid-x">
                    <div class="large-1 medium-2 small-3 cell flexbox-start">
                        <router-link :to="{name: 'dashboard'}">
                            <icon class="arrow-left-circle-svg return-to-dashboard large-svg fill-white stroke-white" name="arrow-left-circle"></icon>
                        </router-link>
                    </div>
                    <div class="large-3 medium-7 small-6 cell flexbox-center-left">
                        <h3><span>Find aktiver</span></h3>
                        <p>Total værdi: {{assetTotalValue | convertToLocalStringDK}} kr.</p>
                    </div>
                </div>
            </header>
            <main class="asset-container-main">
                <div class="grid-x full-height">
                    <div class="large-1 medium-1 small-12 small-cell">
                        &nbsp;
                    </div>
                    <div class="large-7 medium-8 small-12 cell flex-column">
                        <div class="asset-container-main-top">
                            <div class="flex-item align-items">
                                <span>{{assets.length}} aktiv<span v-if="assets.length !== 1">er</span></span>
                            </div>
                            <div class="flex-item">
                                <input ref="input" class="form-control" type="text" v-model="searchString" placeholder="Knudenavn" autofocus
                                    @keydown.enter = 'enter'
                                />
                                <a @click="enter" class="search-icon-input">
                                    <icon class="small-svg" name="search"></icon>
                                </a>
                            </div>
                            <div style="display: flex; align-items: center; margin-right: 20px;">                               
                                <a @click="exportAsExcel()" class="button button-simplified" :disabled="assets.length === 0 || buildingExcel">
                                    <icon v-if="!buildingExcel" class="small-svg fill-primary" name="export"></icon>
                                    <div v-else class="lds-ring">
                                        <div></div>
                                        <div></div>
                                        <div></div>
                                        <div></div>
                                    </div>
                                    Eksportér
                                </a>                    
                            </div>
                        </div>

                        <div class="asset-container-main-list">
                            <div v-if="gettingServerData" class="list-counter grid-x">
                                <div class="no-result full-width">
                                    <div class="large-12 cell">
                                        <span>Henter data fra serveren...</span>
                                    </div>
                                </div>
                            </div>
                            <div v-else-if="errorServerResponse" class="list-counter grid-x">
                                <div class="no-result full-width">
                                    <div class="large-12 cell">
                                        <span>Kunne ikke hente data fra serveren</span>
                                    </div>
                                </div>
                            </div>
                            <div v-else-if="assets.length === 0" class="list-counter grid-x">
                                <div class="no-result full-width">
                                    <div class="large-12 cell">
                                        <span>Ingen aktiver, prøv med et andet:</span>
                                        <ul>
                                            <li>Knudenavn (min. 3 karakterer)</li>
                                            <li>
                                                Marker område i kortet med draw - klik på ikonet:
                                                <icon class="small-svg" style="vertical-align: -3px;" name="draw-map"></icon>
                                            </li>
                                            <li>Max antal aktiver i søgeresultat er 5000.</li>
                                        </ul>
                                    </div>
                                    <div class="grid-x">
                                        <div class="large-12 no-result-img cell"></div>
                                    </div>
                                </div>
                            </div>
                            <div v-else>
                                <div class="grid-x list-sort">
                                    <div class="large-11 cell">
                                        <div class="grid-x" style="padding: 10px;">
                                            <div class="large-5 cell item flexbox" @click="sort('beskrivelse')" :class="{selected: currentSort === 'beskrivelse'}">
                                                <span>Navn</span>
                                                <div>
                                                    <icon v-if="currentSort === 'beskrivelse'" class="small-svg fill-selected-dark-gray" :class="{rotated: currentSortDir === 'desc' && currentSort === 'beskrivelse'}" name="keyboard_arrow_sort"></icon>
                                                </div>
                                            </div>
                                            <div class="large-3 cell item flexbox no-margin" @click="sort('kategoriNavn')" :class="{selected: currentSort === 'kategoriNavn'}">
                                                <span>Kategori</span>
                                                <div>
                                                    <icon v-if="currentSort === 'kategoriNavn'" class="small-svg fill-selected-dark-gray" :class="{rotated: currentSortDir === 'desc' && currentSort === 'kategoriNavn'}" name="keyboard_arrow_sort"></icon>
                                                </div>
                                            </div>
                                            <div class="large-2 cell item flexbox no-margin flex-center" @click="sort('restLevetid')" :class="{selected: currentSort === 'restLevetid'}">
                                                <span>Restlevetid</span>
                                                <div>
                                                    <icon v-if="currentSort === 'restLevetid'" class="small-svg fill-selected-dark-gray" :class="{rotated: currentSortDir === 'desc' && currentSort === 'restLevetid'}" name="keyboard_arrow_sort"></icon>
                                                </div>
                                            </div>
                                            <div class="large-2 cell item flexbox no-margin flex-center" @click="sort('restVaerdi')" :class="{selected: currentSort === 'restVaerdi'}">
                                                <span>Restværdi</span>
                                                <div>
                                                    <icon v-if="currentSort === 'restVaerdi'" class="small-svg fill-selected-dark-gray" :class="{rotated: currentSortDir === 'desc' && currentSort === 'restVaerdi'}" name="keyboard_arrow_sort"></icon>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="large-1 cell small-box"></div>
                                </div>
                                <transition-group name="list" tag="div" v-if="assets && assets.length > 0">
                                    <div v-for="asset in sortedAssets" :key="asset.id" class="grid-x">
                                        <div class="large-11 cell">
                                            <div class="grid-x list-row" @click="goToAssetDetail(asset)">
                                                <div class="large-5 cell flexbox-align-items truncate-fade-blue">
                                                    <span v-html="formatDescription(asset.beskrivelse)"></span>
                                                </div>
                                                <div class="large-3 cell flexbox-align-items truncate-fade-blue">
                                                    {{asset.kategoriNavn}}
                                                </div>
                                                <div class="large-2 cell flexbox-align-items flex-center">
                                                    {{asset.restLevetid}} år
                                                </div>
                                                <div class="large-2 cell flexbox-align-items flex-center">
                                                    {{asset.restVaerdi | convertToLocalStringDK}} kr.
                                                </div>
                                            </div>
                                        </div>
                                        <div v-if="asset.wkt" class="large-1 cell small-box flexbox-center button-highlight" @click="setLocationMap(asset)">
                                            <icon class="small-svg fill-primary-no-stroke" name="location"></icon>
                                        </div>
                                    </div>
                                </transition-group>
                            </div>
                        </div>
                    </div>
                    <div class="auto cell">
                        <nmap :config="mapConfig" @mounted="mapLoaded"/>
                    </div>
                </div>
            </main>
            <project-slider :sliderOpen="sliderOpen" :selectedAsset="selectedAsset" @toggleSlider="toggleSlider"></project-slider>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex'
import axios from '@/store/api.js'
import projectSlider from '@/components/page/project/project-slider.vue'
import { NMap, NMapConfig, NMapUtils, NMapStyle, NMapStyleCircle, NMapLayerControl, NMapLayerEsriWorldImagery, NMapLayerOpenStreetMap, NMapLayerVector, NMapBoundingBox, NMapCoordinates, NMapFeature, NMapButton, NMapDraw } from 'ncomponents-map'

export default {
    name: 'searchAsset',
    components: {
        'project-slider': projectSlider,
        nmap: NMap
    },
    data () {
        return {
            assets: [],
            selectedAsset: null,
            searchString: null,
            searchWkt: null,
            gettingServerData: false,
            errorServerResponse: false,
            // search
            currentSort: 'beskrivelse',
            currentSortDir: 'asc',
            // slider
            sliderOpen: false,
            // map
            mapConfig: null,
            mapLayers: {
                highlightLayer: null,
                featureLayer: null
            },
            assetDataFrom: null,
            buildingExcel: false
        }
    },
    computed: {
        ...mapGetters({
            selectedRegnskab: 'getSelectedRegnskab',
            selectedBalance: 'getSelectedBalance'
        }),
        assetTotalValue () {
            let total = 0
            if (this.assets.length > 0) {
                this.assets.forEach(asset => {
                    total += asset.restVaerdi
                })
            }
            return total
        },
        sortedAssets () {
            return this.assets.sort((a, b) => {
                let modifier = 1
                if (this.currentSortDir === 'desc') {
                    modifier = -1
                }
                if (a[this.currentSort] < b[this.currentSort] || a[this.currentSort] === null) {
                    return -1 * modifier
                }
                if (a[this.currentSort] > b[this.currentSort] || b[this.currentSort] === null) {
                    return 1 * modifier
                }
                return 0
            })
        }
    },
    methods: {
        setLocationMap (asset) {
            this.mapLayers.highlightLayer.clear()
            this.mapConfig.fit(asset.feature, {maxZoom: 20})
            this.mapLayers.highlightLayer.addFeature(new NMapFeature({
                sourceType: 'wktFeature',
                source: asset.wkt,
                projection: 'EPSG:25832',
                style: asset.mapStyle === 'spild_broend'
                    ? new NMapStyleCircle({
                        fillOpacity: 0,
                        strokeColor: 'orange',
                        strokeOpacity: 0.6,
                        strokeWidth: 9
                    })
                    : new NMapStyle({
                        strokeColor: 'orange',
                        strokeOpacity: 0.6,
                        strokeWidth: 9
                    })
            }))

            this.mapLayers.highlightLayer.addFeature(new NMapFeature({
                sourceType: 'wktGeometry',
                source: asset.wkt,
                projection: 'EPSG:25832',
                style: this.getFeatureStyle(asset)
            }))
        },
        async enter () {
            try {
                if (this.searchString.length > 2) {
                    await this.searchAssets()
                }
            } catch (err) {
                console.log('enter err', err)
            }
        },
        async searchAssets () {
            try {
                this.errorServerResponse = false
                this.gettingServerData = true
                this.assets = []
                const response = await axios.post('/Value/Aktiv/SearchAktiv', {
                    regnskabId: this.selectedRegnskab.id,
                    searchQuery: this.searchString
                })
                if (response) {
                    this.assets = response.data
                    this.assetDataFrom = 'search'
                    this.gettingServerData = false
                    this.populateMap()
                }
            } catch (err) {
                this.assets = []
                this.gettingServerData = false
                this.errorServerResponse = true
                throw err
            }
        },
        async readAktivByWkt () {
            try {
                if (this.searchWkt) {
                    this.errorServerResponse = false
                    this.gettingServerData = true
                    this.assets = []
                    const response = await axios.post('/Value/Aktiv/ReadAktivByWkt', {
                        regnskabId: this.selectedRegnskab.id,
                        balanceId: this.selectedBalance.id,
                        wkt: this.searchWkt
                    })
                    if (response) {
                        this.assets = response.data
                        this.assetDataFrom = 'wkt'
                        this.gettingServerData = false
                        this.populateMap()
                    }
                }
            } catch (err) {
                console.log('error reading aktiv by wkt', err)
            }
        },
        goToAssetDetail (asset) {
            this.toggleSlider(true, asset)
        },
        toggleSlider (boolean, asset) {
            this.sliderOpen = boolean
            this.selectedAsset = asset
        },
        sort (s) {
            if (s === this.currentSort) {
                this.currentSortDir = this.currentSortDir === 'asc' ? 'desc' : 'asc'
            }
            this.currentSort = s
        },
        formatDescription (text) {
            if (text) {
                let ledning = text.toLowerCase().includes('ledning:')
                if (ledning) {
                    let newText = ''
                    let parseNext = false
                    let prevText = ''
                    let splitted = text.split(' ')
                    splitted.forEach(t => {
                        if (parseNext) {
                            newText += t + ' '
                            parseNext = false
                        }
                        if (t.toLowerCase() === 'ledning:') {
                            newText += t + ' '
                            parseNext = true
                        }
                        if (t.toLowerCase() === 'fra' || t.toLowerCase() === 'til') {
                            parseNext = true
                        }
                        if (prevText.toLowerCase() === 'ledning:') {
                            newText += '<br />'
                        }
                        if (prevText.toLowerCase() === 'fra') {
                            newText += '- '
                        }

                        prevText = t
                    })
                    return newText
                }
                return text
            }
        },
        async exportAsExcel () {
            this.buildingExcel = true
            try {
                let response
                if (this.assetDataFrom === 'search') {
                    response = await axios.downloadExcel('/Value/Export/ExportAktivSearch ', {regnskabId: this.selectedRegnskab.id, searchQuery: this.searchString})
                } else if (this.assetDataFrom === 'wkt') {
                    response = await axios.downloadExcel('/Value/Export/ExportAktivSearchByWkt ', {regnskabId: this.selectedRegnskab.id, balanceId: null, wkt: this.searchWkt})
                }

                if (response) {
                    return new Promise((resolve, reject) => {
                        let blob = new Blob([response.data], { type: 'application/vnd.ms-excel' })
                        if (window.navigator && window.navigator.msSaveOrOpenBlob) {
                            window.navigator.msSaveOrOpenBlob(blob, 'Aktiver.xlsx')
                            resolve(blob)
                        }

                        var link = document.createElement('a')
                        link.href = window.URL.createObjectURL(blob, 'Aktiver.xlsx')
                        link.download = 'Aktiver.xlsx'
                        document.body.appendChild(link)
                        link.click()
                        document.body.removeChild(link)

                        this.buildingExcel = false
                        resolve(blob)
                    })
                }
            } catch (err) {
                this.buildingExcel = false
                throw err
            }
        },
        mapLoaded () {
            this.mapConfig.fit(new NMapBoundingBox(
                new NMapCoordinates(8.08997684086, 54.8000145534, 'EPSG:4326'),
                new NMapCoordinates(12.6900061378, 57.730016588, 'EPSG:4326')
            ))
        },
        populateMap () {
            if (this.mapLayers.featureLayer) {
                this.mapLayers.featureLayer.clear()
            }
            if (this.assets.length > 0) {
                this.mapLayers.featureLayer = new NMapLayerVector({
                    hidden: true
                })
                this.mapConfig.addLayer(this.mapLayers.featureLayer)
                this.mapLayers.highlightLayer = new NMapLayerVector({
                    hidden: true
                })
                this.mapConfig.addLayer(this.mapLayers.highlightLayer)
                this.assets.forEach(asset => {
                    if (asset.wkt) {
                        const feature = new NMapFeature({
                            sourceType: 'wktGeometry',
                            source: asset.wkt,
                            projection: 'EPSG:25832',
                            style: this.getFeatureStyle(asset)
                        })
                        this.mapLayers.featureLayer.addFeature(feature)
                        asset.feature = feature
                    }
                })
                if (this.mapLayers.featureLayer.features.length > 0) {
                    this.mapConfig.fit(this.mapLayers.featureLayer, {maxZoom: 20, padding: [10, 10, 10, 10]})
                }
            }
        },
        getFeatureStyle (item) {
            let style = new NMapStyle({
                strokeWidth: 2
            })
            switch (item.mapStyle) {
            case 'spild_spildevandsledning':
                style.strokeColor = 'red'
                break

            case 'spild_regnvandsledning':
                style.strokeColor = 'blue'
                style.strokeDash = [10, 10]
                break

            case 'spild_faellesledning':
                style.strokeColor = 'green'
                style.strokeDash = [25, 10]
                break

            case 'spild_draenledning':
                style.strokeColor = 'pink'
                style.strokeDash = [25, 10]
                break

            case 'spild_trykledning-spildevand':
                style.strokeColor = 'red'
                style.strokeDash = [20, 10, 6, 10]
                break

            case 'spild_trykledning-regnvand':
                style.strokeColor = 'blue'
                style.strokeDash = [20, 10, 6, 10]
                break

            case 'spild_nedlagtledning':
                style.strokeColor = 'yellow'
                style.strokeDash = [20, 10, 8, 10]
                break

            case 'spild_broend':
                style = new NMapStyleCircle({
                    fillOpacity: 0,
                    strokeColor: 'black'
                })
                break

            default:
                style.strokeColor = 'black'
                break
            }

            return style
        },
        drawPoint () {
            this.searchWkt = null
            const _this = this
            const draw = new NMapDraw({
                type: 'Polygon',
                style: new NMapStyle({
                    fillColor: 'grey',
                    fillOpacity: 0.3,
                    strokeColor: 'black',
                    radius: 10
                })
            })
            draw.on('done', (e) => {
                this.searchWkt = e.feature.wkt('EPSG:25832')
                _this.readAktivByWkt()
            })
            this.mapConfig.draw(draw)
        }
    },
    created () {
        NMapUtils.registerProjection('EPSG:25832', '+proj=utm +zone=32 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs')
        this.mapConfig = new NMapConfig()
        this.mapConfig.addButton(new NMapLayerControl({
            map: this.mapConfig,
            baseHeading: 'Baggrundskort'
        }))
        const drawButton = new NMapButton({
            content: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 795.67 742.38"><defs><style>.a{fill:none;}</style></defs><title>Tegn polygon i kort</title><path class="a" d="M632.77,307.23,566.3,192.1a96.38,96.38,0,0,1-92.53-45.43H342.88A96.36,96.36,0,0,1,254.77,204a97.44,97.44,0,0,1-13.09-.89l-67.53,117a96.34,96.34,0,0,1,0,113.56l67.53,117a97.44,97.44,0,0,1,13.09-.89,96.36,96.36,0,0,1,88.11,57.33H467.81a96.39,96.39,0,0,1,104.55-55.92l60.41-104.64a96.34,96.34,0,0,1,0-139.26Z"/><path style="fill: white;" d="M795.67,376.86a96.34,96.34,0,0,0-88-96L633.8,153A96.34,96.34,0,1,0,463.62,68.67H342.88A96.34,96.34,0,1,0,175.29,162.1l-68.69,119A96.33,96.33,0,1,0,96.33,473.19a99.59,99.59,0,0,0,10.27-.54l68.69,119a96.34,96.34,0,1,0,167.59,93.43H467.81a96.34,96.34,0,1,0,169.53-90.48l70.29-121.74A96.34,96.34,0,0,0,795.67,376.86Zm-162.9,69.63L572.36,551.13a96.39,96.39,0,0,0-104.55,55.92H342.88a96.36,96.36,0,0,0-88.11-57.33,97.44,97.44,0,0,0-13.09.89l-67.53-117a96.34,96.34,0,0,0,0-113.56l67.53-117a97.44,97.44,0,0,0,13.09.89,96.36,96.36,0,0,0,88.11-57.33H473.77A96.38,96.38,0,0,0,566.3,192.1l66.47,115.13a96.34,96.34,0,0,0,0,139.26Z"/></svg>'
        })
        drawButton.on('click', this.drawPoint)
        this.mapConfig.addButton(drawButton)

        this.mapConfig.addLayer(new NMapLayerOpenStreetMap())
        this.mapConfig.addLayer(new NMapLayerEsriWorldImagery({
            visible: false
        }))
    }
}
</script>

<style lang="scss" scoped>
.asset-container {
    display: flex;
    flex-direction: column;

    .asset-container-header {
        flex: 0 0 auto;
        justify-content: center;
        padding: 20px 0;
        background-color: #367abd;
        color: #fff;
        height: 160px;
        max-height: 160px;
        z-index: 999;

        .flexbox-start {
            display: flex;
            align-items: center;
            justify-content: center;
        }

        .flexbox-center-left {
            display: flex;
            justify-content: center;
            flex-direction: column;
            text-align: left;
        }

        h3 {
            font-weight: 400;
            margin-bottom: 10px;

            span {
                margin-bottom: 10px;
                border-bottom: 1px solid #fff;
            }
        }
    }

    .asset-container-main {
        flex: 1;
        display: flex;
        flex-direction: column;

        .small-cell {
            width: 4%;
        }

        .asset-container-main-top {
            display: flex;
            flex: 0 0 auto;
            flex-direction: row;
            margin-top: 20px;
            margin-bottom: 20px;

            .flex-item {
                display: flex;
                width: 100%;

                input {
                    margin-bottom: 0px !important;
                }
            }

            .align-items {
                align-items: center;
            }
        }

        .asset-container-main-list {
            flex: 1 1 0;
            overflow-y: auto;
        }
    }
}

.flex-column {
    display: flex;
    flex-direction: column;
}

.list-row .cell {
    height: 100%;
}

.list-row:hover {
    .truncate-fade-blue:after {
        background: linear-gradient(to right, rgba(255, 255, 255, 0), #e5eff2 71%) !important;
    }
}

.list-counter {
    height: 95%;

    .no-result {
        margin-top: 20px;
    }

    .no-result-img {
        margin-top: 20px;
    }
}

.list-sort {
    padding-left: 0px;
    padding-right: 0px;
}

.flex-center {
    display: flex;
    justify-content: center;
}

.button-highlight {
    border: 1px solid #eee;
    height: 35px;
    margin-top: 13px;
    margin-left: 6px;
    transition: .3s all ease;
    background-color: #fff;
    box-shadow: 0 3px 6px rgba(0,0,0,0.16), 0 3px 6px rgba(0,0,0,0.23);

    &:hover {
        background-color: #367ABD;
        border-color: #367ABD;

        svg {
            fill: #fff !important;
        }
    }
}
</style>



// WEBPACK FOOTER //
// src/pages/searchAsset.vue